import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sparkles } from "lucide-react";
import { config, scrollToId } from "../config";

const links = [
  { label: "Invitaciones Plus", id: "diferencial" },
  { label: "Ejemplos", id: "ejemplos" },
  { label: "Plantillas", id: "plantillas" },
  { label: "Cómo funciona", id: "como-funciona" },
  { label: "Contacto", id: "contacto" },
];

export const Navbar = ({ onOpenDemo }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    scrollToId(id);
  };

  return (
    <header
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-40 transition-[background-color,box-shadow,border-color] duration-500 ${
        scrolled
          ? "border-b border-slate-200/70 bg-white/85 backdrop-blur-xl"
          : "border-b border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className={`flex items-center gap-2 text-left transition-colors duration-500 ${scrolled ? "text-slate-900" : "text-white"}`}
        >
          <span className="text-lg font-semibold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
            {config.brand.name}
          </span>
          <span
            className={`hidden items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-medium sm:inline-flex ${
              scrolled
                ? "border-indigo-200 bg-indigo-50 text-indigo-700"
                : "border-white/30 bg-white/10 text-white"
            }`}
          >
            <Sparkles className="h-3 w-3" />
            {config.brand.productShort}
          </span>
        </button>

        <nav className="hidden items-center gap-7 lg:flex" aria-label="Navegación principal">
          {links.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className={`text-sm font-medium transition-colors duration-300 ${
                scrolled ? "text-slate-600 hover:text-indigo-600" : "text-white/85 hover:text-white"
              }`}
            >
              {l.label}
            </button>
          ))}
          <button
            data-testid="nav-demo-button"
            onClick={() => onOpenDemo("boda")}
            className="rounded-full bg-[#4F5BD5] px-5 py-2 text-sm font-semibold text-white transition-colors duration-300 hover:bg-[#3B46BC]"
          >
            Ver una invitación
          </button>
        </nav>

        <button
          data-testid="nav-mobile-menu-button"
          aria-label={open ? "Cerrar menú" : "Abrir menú"}
          onClick={() => setOpen(!open)}
          className={`lg:hidden ${scrolled ? "text-slate-900" : "text-white"}`}
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile-menu"
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="overflow-hidden border-b border-slate-200 bg-white/95 backdrop-blur-xl lg:hidden"
          >
            <div className="flex flex-col gap-1 px-4 py-4">
              {links.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-link-${l.id}`}
                  onClick={() => go(l.id)}
                  className="rounded-xl px-3 py-3 text-left text-base font-medium text-slate-700 transition-colors hover:bg-indigo-50 hover:text-indigo-700"
                >
                  {l.label}
                </button>
              ))}
              <button
                data-testid="nav-mobile-demo-button"
                onClick={() => {
                  setOpen(false);
                  onOpenDemo("boda");
                }}
                className="mt-2 rounded-full bg-[#4F5BD5] px-5 py-3 text-center text-sm font-semibold text-white"
              >
                Ver una invitación
              </button>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
};
